import {Injectable} from '@angular/core';
import {BehaviorSubject, lastValueFrom, window} from 'rxjs';
import {v4 as uuidv4} from 'uuid';
import shajs from 'sha.js';
import {HttpClient} from '@angular/common/http';
import {jwtDecode} from 'jwt-decode';
import {environment} from '../../environments/environment';

const KEY_AUTH_TOKENS = 'auth_tokens';
const KEY_AUTH_STATE = 'auth_state';
const KEY_AUTH_USER = 'auth_user';

type AuthResult = {
  accessToken: string
  expiresIn: number
  idToken: string
  refreshToken: string
  tokenType: string
};

@Injectable({
  providedIn: 'root'
})
export class AuthenticationService {

  // TODO this should be fetched from internal api call
  authServiceUrl = environment.authServiceUrl;
  clientId = environment.clientId

  // TODO on web version this should be the window.location, on mobile a deeplink
  redirectUri = new URL('login', location.origin).toString();

  storage = localStorage;

  // Init with null to filter out the first value in a guard!
  isAuthenticated: BehaviorSubject<boolean | null> = new BehaviorSubject<boolean | null>(null);
  private _accessToken?: string;
  private _refreshToken?: string;

  constructor(private http: HttpClient) {
    this.loadToken();
  }

  async loadToken() {
    const accessToken = this.storage.getItem(KEY_AUTH_TOKENS);
    if (accessToken) {
      const tokens: AuthResult = JSON.parse(accessToken);
      this._accessToken = tokens.accessToken;
      this._refreshToken = tokens.refreshToken;
      this.isAuthenticated.next(true);
    } else {
      this._accessToken = null;
      this._refreshToken = null;
      this.isAuthenticated.next(false);
    }
  }

  async login(code?: string, state?: string, returnUrl?: string) {
    // Check if there is an accessToken
    if (this._accessToken) {
      // Check if the accessToken is still valid
      await this.validateAccessToken(this._accessToken);
      // If not try using the refresh token to get a new accessToken
      // If not able to refresh accessToken forward to auth service
      return;
    }

    if (!code) {
      // Not logged in and no code - we are at the start of the auth phase
      // go to login service
      await this.forwardToAuthServiceLogin(returnUrl);
    } else {
      // Code is specified - we are in the callback phase of auth service
      // Exchange the code for access tokens
      await this.exchangeCodeForTokens(code, state);
      // Check if the auth state has any return url, if so return it
      const authorizationState = this.getAuthorizationState();
      if (authorizationState) {
        this.storage.removeItem(KEY_AUTH_STATE);
        if (authorizationState.returnUrl) {
          return authorizationState.returnUrl;
        }
      }
    }
  }

  private async exchangeCodeForTokens(code: string, state: string | undefined) {
    if (!code) return;

    const tokens = await this.getTokens(code, state);
    await this.updateStorageWithTokens(tokens);
  }

  private async updateStorageWithTokens(tokens: AuthResult) {
    if (!tokens) return;

    this.storage.setItem(KEY_AUTH_TOKENS, JSON.stringify(tokens));
    const user = jwtDecode(tokens.idToken);
    this.storage.setItem(KEY_AUTH_USER, JSON.stringify(user));
    await this.loadToken();
    this.isAuthenticated.next(true);
  }

  logout() {
    // Remove all storage entries on logout
    this.storage.removeItem(KEY_AUTH_TOKENS);
    this.storage.removeItem(KEY_AUTH_STATE);
    this.storage.removeItem(KEY_AUTH_USER);
    this._accessToken = null;
    this._refreshToken = null;
    this.isAuthenticated.next(false);
  }

  async forwardToAuthServiceLogin(returnUrl?: string) {
    if (!this.authServiceUrl) return;

    const codeVerifier = uuidv4();
    const authorizeData = {
      codeChallenge: shajs('sha256').update(codeVerifier).digest('hex'),
      state: uuidv4(),
    };
    const params = {
      responseType: 'code',
      codeChallengeMethod: 'S256',
      clientId: this.clientId,
      redirectUri: this.redirectUri,
      ...authorizeData,
    };
    const authData = {
      codeVerifier,
      ...authorizeData,
      returnUrl,
    };

    // Store the auth data in storage - we need this in the auth callback
    this.storage.setItem(KEY_AUTH_STATE, JSON.stringify(authData));

    const url = new URL('authorize', this.authServiceUrl);
    url.search = new URLSearchParams(params).toString();
    open(url.toString(), '_self');
  }

  async getTokens(code: string, state?: string): Promise<AuthResult> {
    try {
      const authorizationData = this.getAuthorizationState();
      if (authorizationData) {
        if (!authorizationData || authorizationData.state !== state) {
          return Promise.reject('State match failed');
        }
      }

      const url = new URL(`${this.authServiceUrl}/oauth/token`);
      const body = {
        grantType: 'authorization_code',
        codeVerifier: authorizationData.codeVerifier,
        code,
        clientId: this.clientId,
        redirectUri: this.redirectUri,
      };
      const tokenResult = this.http.post<AuthResult>(url.toString(), body).pipe(response => response);

      return await lastValueFrom<AuthResult>(tokenResult);
    } catch (err) {
      return Promise.reject(err);
    }
  }

  async refreshAccessToken() {
    try {
      if (!this._refreshToken) {
        // No refresh token forward to login
        return this.login();
      }else{
        const url = new URL(`${this.authServiceUrl}/oauth/token`);
        const body = {
          grantType: 'refresh_token',
          refreshToken: this._refreshToken,
          clientId: this.clientId,
        };
        const tokenResult = this.http.post<AuthResult>(url.toString(), body).pipe(response => response);
        const tokens = await lastValueFrom<AuthResult>(tokenResult);
        await this.updateStorageWithTokens(tokens);
      }
    } catch (err) {
      return Promise.reject(err);
    }
  }

  private getAuthorizationState() {
    const item = this.storage.getItem(KEY_AUTH_STATE);
    if (!item) return;

    return JSON.parse(item);
  }

  async validateAccessToken(accessToken: string) {

  }

  get accessToken(): string | undefined {
    return this._accessToken;
  }

  get refreshToken(): string | undefined {
    return this._refreshToken;
  }

  get user() {
    let userDetailsString = this.storage.getItem(KEY_AUTH_USER);
    if(!userDetailsString) return
    return JSON.parse(userDetailsString);
  }
}
